import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Html } from '@react-three/drei'
import { Play, RotateCcw } from 'lucide-react'
import * as THREE from 'three'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Chuyển động ném (Lớp 10 — Bài 7): ném ngang (từ độ cao h) & ném xiên (đổi góc α).
// Quỹ đạo parabol hiển thị 3D; CHỈ vẽ vệt phía sau vật (phần đã đi), không hiện đường phía trước.
// x = v₀cosα·t ; y = y₀ + v₀sinα·t − ½g·t². g = 9,8 m/s².
const G = 9.8, GW = 660, GH = 150
type P = { vx: number; vy0: number; y0: number; tEnd: number; xmax: number; ymax: number; S: number }

function Projectile({ getP, tRef, runRef, onLand }: { getP: () => P; tRef: React.MutableRefObject<number>; runRef: React.MutableRefObject<boolean>; onLand: () => void }) {
  const ball = useRef<THREE.Mesh>(null)
  const trail = useMemo(() => new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0x2563eb })), [])
  const arrow = useMemo(() => new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), new THREE.Vector3(), 1, 0x16a34a, 0.4, 0.22), [])
  useFrame((_, dt) => {
    const p = getP()
    if (runRef.current && tRef.current < p.tEnd) { tRef.current = Math.min(p.tEnd, tRef.current + Math.min(0.04, dt)); if (tRef.current >= p.tEnd) onLand() }
    const t = tRef.current, S = p.S
    const cx = (x: number) => (x - p.xmax / 2) * S
    const yAt = (tau: number) => (p.y0 + p.vy0 * tau - 0.5 * G * tau * tau) * S
    const px = cx(p.vx * t), py = yAt(t)
    if (ball.current) ball.current.position.set(px, py, 0)
    // vệt PHÍA SAU vật: chỉ các điểm từ 0 → t (đã đi qua)
    const N = 90, pts: THREE.Vector3[] = []
    for (let i = 0; i <= N; i++) { const tau = (t * i) / N; pts.push(new THREE.Vector3(cx(p.vx * tau), yAt(tau), 0)) }
    trail.geometry.setFromPoints(pts)
    // vec tơ vận tốc
    const vy = p.vy0 - G * t, vmag = Math.hypot(p.vx, vy) || 1
    arrow.setDirection(new THREE.Vector3(p.vx / vmag, vy / vmag, 0))
    arrow.position.set(px, py, 0)
    arrow.setLength(Math.min(2.4, vmag * 0.12), 0.4, 0.22)
  })
  return (
    <>
      <primitive object={trail} />
      <primitive object={arrow} />
      <mesh ref={ball}><sphereGeometry args={[0.24, 24, 24]} /><meshStandardMaterial color="#dc2626" metalness={0.2} roughness={0.5} /></mesh>
    </>
  )
}

function Scene({ getP, tRef, runRef, onLand }: { getP: () => P; tRef: React.MutableRefObject<number>; runRef: React.MutableRefObject<boolean>; onLand: () => void }) {
  const p = getP(), S = p.S, x0 = -(p.xmax / 2) * S
  return (
    <>
      <ambientLight intensity={0.8} />
      <directionalLight position={[5, 8, 6]} intensity={0.85} />
      {/* nền đất */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}><planeGeometry args={[20, 16]} /><meshStandardMaterial color="#86c06c" /></mesh>
      <gridHelper args={[20, 20, 0x6f9f57, 0x9ccf82]} position={[0, 0.01, 0]} />
      {/* trục x (mặt đất) & y (thẳng đứng tại điểm ném) */}
      <mesh position={[0, 0.02, 0]}><boxGeometry args={[p.xmax * S + 1, 0.03, 0.03]} /><meshStandardMaterial color="#475569" /></mesh>
      <mesh position={[x0, (p.ymax * S) / 2 + 0.2, 0]}><boxGeometry args={[0.03, p.ymax * S + 0.4, 0.03]} /><meshStandardMaterial color="#475569" /></mesh>
      {/* mũi tên trục x, y */}
      <mesh position={[(p.xmax * S + 1) / 2, 0.02, 0]} rotation={[0, 0, -Math.PI / 2]}><coneGeometry args={[0.08, 0.22, 16]} /><meshStandardMaterial color="#475569" /></mesh>
      <mesh position={[x0, p.ymax * S + 0.51, 0]}><coneGeometry args={[0.08, 0.22, 16]} /><meshStandardMaterial color="#475569" /></mesh>
      <Html position={[x0 - 0.45, p.ymax * S + 0.42, 0]} center><span style={{ color: '#475569', fontWeight: 700, fontSize: 12 }}>y</span></Html>
      <Html position={[(p.xmax / 2) * S + 0.7, 0.1, 0]} center><span style={{ color: '#475569', fontWeight: 700, fontSize: 12 }}>x</span></Html>
      <Projectile getP={getP} tRef={tRef} runRef={runRef} onLand={onLand} />
      <OrbitControls enablePan={false} target={[0, (p.ymax * S) / 2, 0]} minDistance={5} maxDistance={20} />
    </>
  )
}

const ProjectileMotionSimulation: React.FC<Props> = ({
  title = 'Chuyển động ném (3D)',
  description = 'Ném ngang từ độ cao h hoặc ném xiên với góc α. Quỹ đạo 3D parabol — chỉ hiện vệt phía sau vật. Bên dưới là đồ thị toạ độ x(t), y(t). g = 9,8 m/s². Kéo chuột để xoay.',
  onComplete,
}) => {
  const gcv = useRef<HTMLCanvasElement>(null)
  const graphRaf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [type, setType] = useState<'xien' | 'ngang'>('xien')
  const [v0, setV0] = useState(18)
  const [angle, setAngle] = useState(45)
  const [h, setH] = useState(30)
  const [running, setRunning] = useState(true)
  const refs = useRef({ type, v0, angle, h })
  refs.current = { type, v0, angle, h }
  const runRef = useRef(running)
  useEffect(() => { runRef.current = running }, [running])
  const firstRef = useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const getP = useCallback((): P => {
    const { type, v0, angle, h } = refs.current
    let vx: number, vy0: number, y0: number, tEnd: number, xmax: number, ymax: number
    if (type === 'ngang') { vx = v0; vy0 = 0; y0 = h; tEnd = Math.sqrt((2 * h) / G); xmax = v0 * tEnd; ymax = h }
    else { const a = (angle * Math.PI) / 180; vx = v0 * Math.cos(a); vy0 = v0 * Math.sin(a); y0 = 0; tEnd = (2 * vy0) / G || 0.1; xmax = vx * tEnd; ymax = (vy0 * vy0) / (2 * G) }
    const S = 9 / Math.max(xmax, ymax, 1)
    return { vx, vy0, y0, tEnd, xmax, ymax, S }
  }, [])

  // Đồ thị toạ độ x(t), y(t) (2D, phía dưới)
  const drawGraph = useCallback(() => {
    const ctx = prepCanvas(gcv.current, GW, GH); if (!ctx) return
    ctx.clearRect(0, 0, GW, GH)
    const p = getP(), t = tRef.current, x0 = 46, y0 = GH - 24, plotW = GW - x0 - 14, plotH = GH - 40
    const vmax = Math.max(1, p.xmax, p.ymax, p.y0)
    const X = (tt: number) => x0 + (tt / p.tEnd) * plotW
    const Y = (val: number) => y0 - (val / vmax) * plotH
    ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1
    for (let gi = 1; gi <= 3; gi++) { const gy = y0 - (gi / 3) * plotH; ctx.beginPath(); ctx.moveTo(x0, gy); ctx.lineTo(x0 + plotW, gy); ctx.stroke() }
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 1.8; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(x0, 10); ctx.lineTo(x0, y0); ctx.lineTo(x0 + plotW, y0); ctx.stroke()
    // mũi tên 2 trục
    ctx.beginPath(); ctx.moveTo(x0, 5); ctx.lineTo(x0 - 4, 13); ctx.lineTo(x0 + 4, 13); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(x0 + plotW + 6, y0); ctx.lineTo(x0 + plotW - 2, y0 - 4); ctx.lineTo(x0 + plotW - 2, y0 + 4); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 11px sans-serif'; ctx.fillText('toạ độ (m)', x0 + 4, 10); ctx.fillStyle = '#64748b'; ctx.font = '10px sans-serif'; ctx.fillText('t (s)', x0 + plotW - 20, y0 + 16)
    const curve = (f: (tt: number) => number, color: string) => {
      ctx.strokeStyle = color; ctx.lineWidth = 2.5; ctx.beginPath()
      for (let i = 0; i <= 80; i++) { const tt = (t * i) / 80; const px = X(tt), py = Y(f(tt)); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) }
      ctx.stroke(); ctx.fillStyle = color; ctx.beginPath(); ctx.arc(X(t), Y(f(t)), 4, 0, Math.PI * 2); ctx.fill()
    }
    curve((tt) => p.vx * tt, '#2563eb')
    curve((tt) => p.y0 + p.vy0 * tt - 0.5 * G * tt * tt, '#dc2626')
    ctx.font = 'bold 11px sans-serif'; ctx.fillStyle = '#2563eb'; ctx.fillText('■ x(t)', GW - 110, 14); ctx.fillStyle = '#dc2626'; ctx.fillText('■ y(t)', GW - 58, 14)
  }, [getP])
  useEffect(() => { const loop = () => { drawGraph(); graphRaf.current = requestAnimationFrame(loop) }; graphRaf.current = requestAnimationFrame(loop); return () => { if (graphRaf.current) cancelAnimationFrame(graphRaf.current) } }, [drawGraph])
  useEffect(() => { tRef.current = 0 }, [type, v0, angle, h])

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="rounded-lg overflow-hidden border border-gray-100" style={{ height: 320, background: 'linear-gradient(#cfeaff,#eef7ff)' }}>
        <Canvas camera={{ position: [0, 4, 13], fov: 50 }} onCreated={touch}>
          <Scene getP={getP} tRef={tRef} runRef={runRef} onLand={() => setRunning(false)} />
        </Canvas>
      </div>
      <canvas ref={gcv} width={GW} height={GH} className="w-full mt-2 rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Kiểu ném</span><select value={type} onChange={(e) => { touch(); setType(e.target.value as 'xien' | 'ngang') }} className="flex-1 px-2 py-1 rounded-lg border border-gray-200 bg-white"><option value="xien">Ném xiên</option><option value="ngang">Ném ngang</option></select></label>
        <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Vận tốc v₀</span><input type="range" min={6} max={28} step={1} value={v0} onChange={(e) => { touch(); setV0(Number(e.target.value)) }} className="flex-1" /><span className="w-14 text-right font-semibold">{v0} m/s</span></label>
        {type === 'xien'
          ? <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Góc ném α</span><input type="range" min={10} max={80} step={5} value={angle} onChange={(e) => { touch(); setAngle(Number(e.target.value)) }} className="flex-1" /><span className="w-14 text-right font-semibold">{angle}°</span></label>
          : <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Độ cao h</span><input type="range" min={10} max={60} step={5} value={h} onChange={(e) => { touch(); setH(Number(e.target.value)) }} className="flex-1" /><span className="w-14 text-right font-semibold">{h} m</span></label>}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); if (tRef.current >= getP().tEnd) tRef.current = 0; setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700"><Play className="w-4 h-4" /> {running ? 'Dừng' : 'Ném'}</button>
        <button onClick={() => { tRef.current = 0 }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Ném lại</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Vệt xanh là phần quỹ đạo vật ĐÃ đi qua (phía sau vật) — không hiện trước đường đi. Mũi tên xanh lá là vec tơ vận tốc (tiếp tuyến quỹ đạo).</p>
    </div>
  )
}
export default ProjectileMotionSimulation
